import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function PublicLeagueRedirect() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const redirectToSeason = async () => {
      if (!leagueId) {
        setError('Invalid league URL');
        return;
      }

      try {
        // First try to get active season
        const { data: activeSeason } = await supabase
          .from('seasons')
          .select('id')
          .eq('league_id', leagueId)
          .eq('is_active', true)
          .eq('is_archived', false)
          .maybeSingle();

        if (activeSeason) {
          // Preserve any query string (e.g. ?tab=standings) so deep links into
          // a specific tab survive the season redirect.
          navigate(`/public/${leagueId}/${activeSeason.id}${location.search}`, { replace: true });
          return;
        }

        // If no active season, get most recent season (including archived)
        const { data: recentSeason } = await supabase
          .from('seasons')
          .select('id')
          .eq('league_id', leagueId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (recentSeason) {
          navigate(`/public/${leagueId}/${recentSeason.id}${location.search}`, { replace: true });
          return;
        }

        // No seasons found at all
        setError('No seasons available for this league');
      } catch (err) {
        console.error('Error redirecting to season:', err);
        setError('Failed to load league');
      }
    };

    redirectToSeason();
  }, [leagueId, navigate, location.search]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <CardTitle>League Not Available</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Loading league...</p>
      </div>
    </div>
  );
}
